/* eslint-disable react/prop-types */
import * as React from 'react';

import { useDebounceFn } from 'ahooks';
import { Form, Input, Space, Typography } from 'antd';

import { AntdRouterExternalLink, AntdUpload, CheckInputbox } from 'components';
import { cn, inputRule } from 'utils';

function PrimitiveForm<T>({
  labelWrap = true,
  layout = 'vertical',
  autoComplete = 'off',
  className,
  requiredMark = (labelNode, { required }) => (
    <Space className='group' align='center' size={4} data-required={required}>
      <span className='absolute top-1 text-sm text-[--ant-red] group-data-[required=false]:hidden'>
        *
      </span>
      <Typography.Text className='group-data-[required=true]:ms-1.5'>
        {labelNode}
      </Typography.Text>
    </Space>
  ),
  ...props
}: React.ComponentPropsWithoutRef<typeof Form<T>>) {
  return (
    <Form<T>
      labelWrap={labelWrap}
      layout={layout}
      autoComplete={autoComplete}
      className={cn('my-2', className)}
      requiredMark={requiredMark}
      {...props}
    />
  );
}

interface PrimitiveItemSeoTitleProps<T>
  extends StrictOmit<
    React.ComponentPropsWithoutRef<typeof Form.Item<T>>,
    'help' | 'children'
  > {
  helpText?: string;
  onDebounceChange?: (value: string) => void;
}

function PrimitiveItemSeoTitle<T>({
  label = 'عنوان سئو (SEO Title)',
  tooltip = 'این عنوان در خط اول نتایج جستجو نمایش داده می‌شود و باید کوتاه و جذاب باشد.',
  helpText = '',
  onDebounceChange,
  ...props
}: PrimitiveItemSeoTitleProps<T>) {
  const { run: debounceChange } = useDebounceFn<
    NonNullable<typeof onDebounceChange>
  >(value => onDebounceChange?.(value));

  const helpTextLength = helpText.length;
  const maxSeoTitleLength = inputRule.seoTitle.max;
  const hasSeoTitleWarning = helpTextLength > maxSeoTitleLength;

  return (
    <Form.Item<T>
      label={label}
      tooltip={tooltip}
      help={
        <Typography.Paragraph className='mt-0.5 flex justify-between gap-4'>
          <Typography.Text type='secondary' className='text-justify'>
            {helpText}
          </Typography.Text>
          <Typography.Text
            type={hasSeoTitleWarning ? 'warning' : undefined}
            className='shrink-0'
          >
            {`${helpTextLength}/${maxSeoTitleLength}`}
          </Typography.Text>
        </Typography.Paragraph>
      }
      {...props}
    >
      <Input
        placeholder='عنوان سئو را وارد کنید'
        className={cn(hasSeoTitleWarning && 'border-[--ant-color-warning]')}
        onChange={({ target: { value } }) => debounceChange(value)}
      />
    </Form.Item>
  );
}

interface PrimitiveItemSeoDescriptionProps<T>
  extends StrictOmit<
    React.ComponentPropsWithoutRef<typeof Form.Item<T>>,
    'help' | 'children'
  > {
  helpText?: string;
  onDebounceChange?: (value: string) => void;
}

function PrimitiveItemSeoDescription<T>({
  label = 'توضیحات متا (Meta Description)',
  tooltip = 'این توضیح در نتایج جستجو نمایش داده می‌شود و باید کاربر را به کلیک ترغیب کند.',
  helpText = '',
  onDebounceChange,
  ...props
}: PrimitiveItemSeoDescriptionProps<T>) {
  const { run: debounceChange } = useDebounceFn<
    NonNullable<typeof onDebounceChange>
  >(value => onDebounceChange?.(value));

  const helpTextLength = helpText.length;
  const maxSeoDescriptionLength = inputRule.seoDescription.max;
  const hasSeoDescriptionWarning = helpTextLength > maxSeoDescriptionLength;

  return (
    <Form.Item<T>
      label={label}
      tooltip={tooltip}
      help={
        <Typography.Paragraph className='mt-0.5 flex justify-between gap-4'>
          <Typography.Text type='secondary' className='text-justify'>
            {helpText}
          </Typography.Text>
          <Typography.Text
            type={hasSeoDescriptionWarning ? 'warning' : undefined}
            className='shrink-0'
          >
            {`${helpTextLength}/${maxSeoDescriptionLength}`}
          </Typography.Text>
        </Typography.Paragraph>
      }
      {...props}
    >
      <Input.TextArea
        autoSize
        placeholder='توضیحات متا را وارد کنید'
        className={cn(
          hasSeoDescriptionWarning && 'border-[--ant-color-warning]',
        )}
        onChange={({ target: { value } }) => debounceChange(value)}
      />
    </Form.Item>
  );
}

interface PrimitiveItemSeoIndexProps<T>
  extends StrictOmit<
    React.ComponentPropsWithoutRef<typeof Form.Item<T>>,
    'children'
  > {
  onDebounceChange?: (value: boolean) => void;
}

function PrimitiveItemSeoIndex<T>({
  label = 'نمایه‌گذاری (Robots Index)',
  tooltip = 'فعال‌سازی این گزینه باعث می‌شود صفحه در نتایج جستجو نمایش داده شود.',
  valuePropName = 'checked',
  onDebounceChange,
  ...props
}: PrimitiveItemSeoIndexProps<T>) {
  const { run: debounceChange } = useDebounceFn<
    NonNullable<typeof onDebounceChange>
  >(value => onDebounceChange?.(value));

  return (
    <Form.Item<T>
      label={label}
      tooltip={tooltip}
      valuePropName={valuePropName}
      {...props}
    >
      <CheckInputbox
        onChange={({ target: { checked } }) => debounceChange(checked)}
      >
        فعال‌سازی نمایه‌گذاری
      </CheckInputbox>
    </Form.Item>
  );
}

interface PrimitiveItemSeoAdvancedProps<T>
  extends StrictOmit<
    React.ComponentPropsWithoutRef<typeof Form.Item<T>>,
    'children'
  > {
  onDebounceChange?: (value: boolean) => void;
}

function PrimitiveItemSeoAdvanced<T>({
  label = 'حالت پیشرفته (Advanced Mode)',
  tooltip = (
    <div>
      در حالت پیشرفته، با استفاده از متغیرهایی مانند{' '}
      <Typography.Text code className='text-inherit'>
        {'{info-name}'}
      </Typography.Text>{' '}
      یا{' '}
      <Typography.Text code className='text-inherit'>
        {'{date-yyyy}'}
      </Typography.Text>{' '}
      (
      <AntdRouterExternalLink href='https://github.com/date-fns-jalali/date-fns-jalali'>
        مستندات date-fns
      </AntdRouterExternalLink>
      ) در عنوان یا توضیحات سئو، به صورت خودکار اطلاعات تولید کنید.
    </div>
  ),
  valuePropName = 'checked',
  onDebounceChange,
  ...props
}: PrimitiveItemSeoAdvancedProps<T>) {
  const { run: debounceChange } = useDebounceFn<
    NonNullable<typeof onDebounceChange>
  >(value => onDebounceChange?.(value));

  return (
    <Form.Item<T>
      label={label}
      tooltip={tooltip}
      valuePropName={valuePropName}
      {...props}
    >
      <CheckInputbox
        onChange={({ target: { checked } }) => debounceChange(checked)}
      >
        فعال‌سازی حالت پیشرفته
      </CheckInputbox>
    </Form.Item>
  );
}

interface PrimitiveItemSeoOpenGraphProps<T>
  extends StrictOmit<
    React.ComponentPropsWithoutRef<typeof Form.Item<T>>,
    'children'
  > {
  uploadProps?: React.ComponentPropsWithoutRef<typeof AntdUpload>;
}

function PrimitiveItemSeoOpenGraph<T>({
  label = 'تصویر رسانه‌ای (Open Graph)',
  tooltip = 'این تصویر هنگام به اشتراک‌گذاری صفحه در شبکه‌های اجتماعی نمایش داده می‌شود. اندازه توصیه شده: 1200x630 پیکسل.',
  valuePropName = 'file',
  uploadProps,
  ...props
}: PrimitiveItemSeoOpenGraphProps<T>) {
  return (
    <Form.Item<T>
      label={label}
      tooltip={tooltip}
      valuePropName={valuePropName}
      {...props}
    >
      <AntdUpload {...uploadProps} />
    </Form.Item>
  );
}

export {
  PrimitiveForm,
  PrimitiveItemSeoAdvanced,
  PrimitiveItemSeoDescription,
  PrimitiveItemSeoIndex,
  PrimitiveItemSeoOpenGraph,
  PrimitiveItemSeoTitle,
};
